import useSWR from 'swr';

import { fetchEndpointData } from '@/utils/fetch.client';

// ── Types ────────────────────────────────────────────────────────────

/** How the agent should run a playbook. Mirrors the backend `AgentMode`. */
export type AgentMode = 'agent' | 'plan' | 'ask';

export const AGENT_MODES: { value: AgentMode; label: string; description: string }[] = [
  { value: 'agent', label: 'Agent', description: 'Full autonomous agent — read, edit, and run commands.' },
  { value: 'plan', label: 'Plan', description: 'Draft a plan first, then execute it.' },
  { value: 'ask', label: 'Ask', description: 'Conversational only — answer questions, no edits.' },
];

/** Playbook metadata as returned by the list endpoint (no markdown body). */
export interface Playbook {
  id: string;
  team_id: string;
  created_by: string;
  name: string;
  macro: string | null;
  agent_mode: AgentMode;
  created_at: string;
  updated_at: string;
}

/** A playbook with its offloaded markdown body, returned by get/create/update. */
export interface PlaybookDetail extends Playbook {
  content: string;
}

export interface PlaybookInput {
  name: string;
  content: string;
  macro?: string | null;
  agent_mode: AgentMode;
}

interface ListPlaybooksResponse {
  playbooks: Playbook[];
}

interface PlaybookResponse {
  playbook: PlaybookDetail;
}

// ── SWR Hooks ──────────────────────────────────────────────────────────

/** List a team's playbooks. Pass `null` to skip fetching (e.g. no team yet). */
export function usePlaybooks(teamId: string | null) {
  const { data, mutate, isLoading } = useSWR(teamId ? `/api/v1/teams/${teamId}/playbooks` : null, () =>
    fetchEndpointData<ListPlaybooksResponse>(`/api/v1/teams/${teamId}/playbooks`),
  );
  return { playbooks: data?.playbooks ?? [], mutate, isLoading };
}

// ── Action Functions ───────────────────────────────────────────────────

export async function getPlaybook(teamId: string, playbookId: string): Promise<PlaybookDetail> {
  const res = await fetchEndpointData<PlaybookResponse>(`/api/v1/teams/${teamId}/playbooks/${playbookId}`);
  return res.playbook;
}

export async function createPlaybook(teamId: string, body: PlaybookInput): Promise<PlaybookDetail> {
  const res = await fetchEndpointData<PlaybookResponse>(`/api/v1/teams/${teamId}/playbooks`, {
    method: 'POST',
    body,
  });
  return res.playbook;
}

export async function updatePlaybook(teamId: string, playbookId: string, body: PlaybookInput): Promise<PlaybookDetail> {
  const res = await fetchEndpointData<PlaybookResponse>(`/api/v1/teams/${teamId}/playbooks/${playbookId}`, {
    method: 'PUT',
    body,
  });
  return res.playbook;
}

export async function deletePlaybook(teamId: string, playbookId: string) {
  return fetchEndpointData(`/api/v1/teams/${teamId}/playbooks/${playbookId}`, { method: 'DELETE' });
}
